import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { SEO } from "../components/SEO";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Mail, Lock, User, Globe, MapPin, Compass, Plane, Shield,
  ChevronDown, Sparkles, CalendarDays, Wallet, ArrowRight, Check
} from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1649606680369-99b40c91b829?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxiZWF1dGlmdWwlMjB0cmF2ZWwlMjBzdW5zZXQlMjBiZWFjaCUyMG1vdW50YWlucyUyMGFkdmVudHVyZSUyMGV4cGxvcmVyJTIwY29sb3JmdWx8ZW58MHx8fHwxNzcxOTk4NjQzfDA&ixlib=rb-4.1.0&q=85";

const GALLERY = [
  { src: "https://images.unsplash.com/photo-1710915322745-cd5912851417?crop=entropy&cs=srgb&fm=jpg&w=600&q=80", alt: "Hot air balloons" },
  { src: "https://images.pexels.com/photos/14783333/pexels-photo-14783333.jpeg?auto=compress&cs=tinysrgb&w=600", alt: "Tropical island aerial" },
  { src: "https://images.unsplash.com/photo-1761068656245-c117787e8826?crop=entropy&cs=srgb&fm=jpg&w=600&q=80", alt: "Colorful waterfront" },
  { src: "https://images.pexels.com/photos/27777255/pexels-photo-27777255.jpeg?auto=compress&cs=tinysrgb&w=600", alt: "Alpine lake" },
  { src: "https://images.unsplash.com/photo-1633829918443-1b625c8fe03b?crop=entropy&cs=srgb&fm=jpg&w=600&q=80", alt: "Mountain sunset" },
  { src: "https://images.pexels.com/photos/5659662/pexels-photo-5659662.jpeg?auto=compress&cs=tinysrgb&w=600", alt: "Beach sunset" },
  { src: "https://images.unsplash.com/photo-1762781838415-a0afaa335397?crop=entropy&cs=srgb&fm=jpg&w=600&q=80", alt: "Colorful buildings" },
  { src: "https://images.unsplash.com/photo-1759521626408-4118f14632ac?crop=entropy&cs=srgb&fm=jpg&w=600&q=80", alt: "Kayaking turquoise waters" },
];

const FEATURES = [
  { icon: Sparkles, title: "AI Trip Planner", desc: "Generate personalized itineraries instantly — just enter your destination and dates", iconColor: "text-violet-400", bg: "bg-violet-500/15" },
  { icon: MapPin, title: "Road Trip Planner", desc: "Plan multi-stop road trips with sights, photos, and interactive route maps", iconColor: "text-emerald-400", bg: "bg-emerald-500/15" },
  { icon: Plane, title: "Flight & Hotel Organizer", desc: "Store tickets, bookings, and confirmations — scan barcodes or import from email", iconColor: "text-sky-400", bg: "bg-sky-500/15" },
  { icon: Shield, title: "Visa Checker & Documents", desc: "Check visa requirements for 199 passports. Passport expiry alerts and reminders", iconColor: "text-amber-400", bg: "bg-amber-500/15" },
  { icon: CalendarDays, title: "Travel Calendar & Journal", desc: "Plan activities, keep a photo travel diary, and get automatic email reminders", iconColor: "text-rose-400", bg: "bg-rose-500/15" },
  { icon: Wallet, title: "Budget & Currency Tracker", desc: "Track travel expenses by category with built-in currency converter for 170+ currencies", iconColor: "text-cyan-400", bg: "bg-cyan-500/15" },
];

const STATS = [
  { value: "199", label: "Passports Covered", color: "from-violet-400 to-fuchsia-400" },
  { value: "AI", label: "Powered Planning", color: "from-cyan-400 to-blue-400" },
  { value: "170+", label: "Currencies", color: "from-emerald-400 to-teal-400" },
  { value: "Free", label: "To Get Started", color: "from-amber-400 to-orange-400" },
];

const Landing = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({ name: "", email: "", password: "" });
  const authRef = useRef(null);

  // 2FA state
  const [twoFaState, setTwoFaState] = useState(null); // { temp_token, email }
  const [twoFaCode, setTwoFaCode] = useState("");
  const [twoFaResending, setTwoFaResending] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);

  // Password strength checker
  const passwordChecks = useMemo(() => {
    const pw = registerForm.password;
    return [
      { label: "At least 12 characters", met: pw.length >= 12 },
      { label: "One uppercase letter (A-Z)", met: /[A-Z]/.test(pw) },
      { label: "One lowercase letter (a-z)", met: /[a-z]/.test(pw) },
      { label: "One number (0-9)", met: /[0-9]/.test(pw) },
      { label: "One special character (!@#$...)", met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(pw) },
    ];
  }, [registerForm.password]);

  useEffect(() => {
    if (user) {
      const returnTo = sessionStorage.getItem("returnTo");
      sessionStorage.removeItem("returnTo");
      navigate(returnTo || "/dashboard", { replace: true });
    }
  }, [user, navigate]);

  if (user) return null;

  const scrollToAuth = () => authRef.current?.scrollIntoView({ behavior: "smooth" });

  const handleGoogleLogin = () => {
    const returnTo = sessionStorage.getItem("returnTo") || "/dashboard";
    const redirectUrl = window.location.origin + returnTo;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginForm, { withCredentials: true });
      if (response.data.requires_2fa) {
        setTwoFaState({ temp_token: response.data.temp_token, email: response.data.email });
        setTwoFaCode("");
        toast.success("Verification code sent to your email");
      } else if (response.data.token) {
        login(response.data);
        toast.success("Welcome back!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2fa = async (e) => {
    e.preventDefault();
    if (!twoFaCode || twoFaCode.length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/verify`, {
        temp_token: twoFaState.temp_token,
        code: twoFaCode,
        remember_device: rememberDevice,
      }, { withCredentials: true });
      if (response.data.token) {
        login(response.data);
        toast.success("Welcome back!");
        setTwoFaState(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend2fa = async () => {
    setTwoFaResending(true);
    try {
      await axios.post(`${API}/auth/2fa/resend`, { temp_token: twoFaState.temp_token });
      toast.success("New code sent to your email");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend code");
    } finally {
      setTwoFaResending(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, registerForm, { withCredentials: true });
      login(response.data);
      toast.success("Account created successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080d19] text-white overflow-x-hidden">
      <SEO
        path="/"
        description="Plan your next trip with AI. Free travel planner with itinerary generator, visa checker for 199 countries, packing lists, budget tracker, travel journal, and more."
      />
      {/* ====== HERO SECTION ====== */}
      <section className="relative min-h-screen flex flex-col">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" style={{ filter: "brightness(0.65) saturate(1.6)" }} />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0f1a]/20 via-transparent to-[#0f1729]/90" />
        </div>

        {/* Nav */}
        <nav className="relative z-20 flex items-center justify-between px-6 lg:px-12 py-5">
          <div className="flex items-center gap-3">
            <img src="/logo192.png" alt="Explorio" className="w-9 h-9 rounded-xl" />
            <span className="text-lg font-bold tracking-tight">Explorio</span>
          </div>
          <Button
            onClick={scrollToAuth}
            variant="outline"
            className="border-white/20 bg-white/5 hover:bg-white/10 text-white text-sm rounded-full px-5 h-9 backdrop-blur-sm"
            data-testid="nav-get-started"
          >
            Log in
          </Button>
        </nav>

        {/* Hero content */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 pb-24">
          <p className="text-cyan-400 text-sm font-medium tracking-[0.2em] uppercase mb-5" style={{ animation: "fadeInUp 0.6s ease-out both" }}>
            Free AI Trip Planner & Travel Organizer
          </p>
          <h1
            className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.1] mb-6 max-w-3xl"
            style={{ animation: "fadeInUp 0.6s ease-out 0.1s both" }}
          >
            Plan your next trip{" "}
            <span className="bg-gradient-to-r from-cyan-400 via-teal-300 to-amber-400 bg-clip-text text-transparent">with AI</span>
          </h1>
          <p
            className="text-lg text-slate-300 max-w-xl mb-10 leading-relaxed"
            style={{ animation: "fadeInUp 0.6s ease-out 0.2s both" }}
          >
            Generate itineraries, check visa requirements for 199 countries, track travel budgets, keep a digital travel journal, and organize all your documents — free.
          </p>
          <div style={{ animation: "fadeInUp 0.6s ease-out 0.3s both" }}>
            <Button
              onClick={scrollToAuth}
              className="h-12 px-8 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-medium text-base shadow-lg shadow-cyan-500/25 transition-all"
              data-testid="hero-get-started"
            >
              Start Your Journey
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <ChevronDown className="w-6 h-6 text-white/40" />
        </div>
      </section>

      {/* ====== SCROLLING PHOTO STRIP ====== */}
      <section className="py-4 overflow-hidden bg-[#080d19]">
        <div className="flex gap-3 animate-scroll-x">
          {[...GALLERY, ...GALLERY].map((img, i) => (
            <img
              key={i}
              src={img.src}
              alt={img.alt}
              className="w-56 h-36 sm:w-64 sm:h-40 object-cover rounded-xl flex-shrink-0 hover:scale-105 transition-transform"
              loading="lazy"
            />
          ))}
        </div>
      </section>

      {/* ====== AUTH SECTION ====== */}
      <section ref={authRef} className="py-16 lg:py-20 px-6" id="auth">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <img src="/logo192.png" alt="Explorio" className="w-14 h-14 rounded-2xl mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Start your journey</h2>
            <p className="text-sm text-slate-500">Create a free account or sign in</p>
          </div>

          <div className="rounded-2xl border border-white/12 bg-[#1a2440]/80 backdrop-blur-xl p-7 shadow-2xl shadow-black/30">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-white/5 rounded-lg p-1">
                <TabsTrigger value="login" className="rounded-md text-sm data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-300" data-testid="login-tab">Sign In</TabsTrigger>
                <TabsTrigger value="register" className="rounded-md text-sm data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-300" data-testid="register-tab">Create Account</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                {twoFaState ? (
                  <form onSubmit={handleVerify2fa} className="space-y-4">
                    <div className="text-center mb-2">
                      <div className="w-12 h-12 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mx-auto mb-3">
                        <Shield className="w-6 h-6 text-cyan-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-white">Verify your identity</h3>
                      <p className="text-xs text-slate-400 mt-1">Enter the 6-digit code sent to<br/><span className="text-cyan-400">{twoFaState.email}</span></p>
                    </div>
                    <div className="space-y-1.5">
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        placeholder="000000"
                        className="glass-input h-14 text-center text-2xl tracking-[0.5em] font-mono"
                        value={twoFaCode}
                        onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        autoFocus
                        data-testid="2fa-code-input"
                      />
                    </div>
                    <Button type="submit" className="w-full h-11 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-medium rounded-lg" disabled={isLoading || twoFaCode.length !== 6} data-testid="2fa-verify-btn">
                      {isLoading ? "Verifying..." : "Verify & Sign In"}
                    </Button>
                    <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="remember-device-label">
                      <input
                        type="checkbox"
                        checked={rememberDevice}
                        onChange={(e) => setRememberDevice(e.target.checked)}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500"
                        data-testid="remember-device-checkbox"
                      />
                      <span className="text-xs text-slate-400">Remember this device for 30 days</span>
                    </label>
                    <div className="flex items-center justify-between text-xs">
                      <button type="button" onClick={handleResend2fa} disabled={twoFaResending} className="text-slate-500 hover:text-cyan-400 transition-colors" data-testid="2fa-resend-btn">
                        {twoFaResending ? "Sending..." : "Resend code"}
                      </button>
                      <button type="button" onClick={() => { setTwoFaState(null); setTwoFaCode(""); }} className="text-slate-500 hover:text-cyan-400 transition-colors" data-testid="2fa-back-btn">
                        Back to login
                      </button>
                    </div>
                  </form>
                ) : (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="login-email" className="text-slate-400 text-xs">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input id="login-email" type="email" placeholder="you@email.com" className="pl-10 glass-input h-11" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} required data-testid="login-email" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="login-password" className="text-slate-400 text-xs">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input id="login-password" type="password" placeholder="Enter password" className="pl-10 glass-input h-11" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} required data-testid="login-password" />
                    </div>
                  </div>
                  <Button type="submit" className="w-full h-11 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-medium rounded-lg shadow-lg shadow-cyan-500/20 transition-all" disabled={isLoading} data-testid="login-submit">
                    {isLoading ? "Signing in..." : "Sign In"}
                  </Button>
                  <div className="text-center">
                    <button type="button" onClick={() => navigate("/forgot-password")} className="text-xs text-slate-500 hover:text-cyan-400 transition-colors" data-testid="forgot-password-link">Forgot your password?</button>
                  </div>
                </form>
                )}
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="register-name" className="text-slate-400 text-xs">Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input id="register-name" type="text" placeholder="Your name" className="pl-10 glass-input h-11" value={registerForm.name} onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })} required data-testid="register-name" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="register-email" className="text-slate-400 text-xs">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input id="register-email" type="email" placeholder="you@email.com" className="pl-10 glass-input h-11" value={registerForm.email} onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })} required data-testid="register-email" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="register-password" className="text-slate-400 text-xs">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input id="register-password" type="password" placeholder="Create password" className="pl-10 glass-input h-11" value={registerForm.password} onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })} required data-testid="register-password" />
                    </div>
                    {registerForm.password.length > 0 && (
                      <div className="mt-2 space-y-1" data-testid="password-checklist">
                        {passwordChecks.map((check, i) => (
                          <div key={i} className={`flex items-center gap-2 text-xs transition-colors ${check.met ? "text-emerald-400" : "text-slate-500"}`} data-testid={`pw-check-${i}`}>
                            <Check className={`w-3 h-3 flex-shrink-0 ${check.met ? "text-emerald-400" : "text-slate-600"}`} />
                            <span>{check.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button type="submit" className="w-full h-11 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-medium rounded-lg shadow-lg shadow-cyan-500/20 transition-all" disabled={isLoading || !passwordChecks.every(c => c.met)} data-testid="register-submit">
                    {isLoading ? "Creating account..." : "Create Account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10"></div></div>
              <div className="relative flex justify-center"><span className="px-3 text-xs text-slate-500 bg-[#1a2440]/80">or</span></div>
            </div>

            <Button onClick={handleGoogleLogin} variant="outline" className="w-full h-11 border-white/10 bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg transition-all flex items-center justify-center gap-2" data-testid="google-login">
              <Globe className="w-4 h-4 text-cyan-400" />
              Continue with Google
            </Button>
          </div>
        </div>
      </section>

      {/* ====== FEATURES SECTION ====== */}
      <section className="py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-cyan-400 text-sm font-medium tracking-widest uppercase mb-3">Everything you need</p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              All your travel tools,{" "}
              <span className="text-slate-400">one app</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map(({ icon: Icon, title, desc, iconColor, bg }) => (
              <div
                key={title}
                className="group p-6 rounded-2xl border border-white/8 bg-white/[0.04] hover:bg-white/[0.08] hover:border-white/15 transition-all"
              >
                <div className={`w-11 h-11 rounded-xl ${bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon className={`w-5 h-5 ${iconColor}`} />
                </div>
                <h3 className="text-base font-semibold mb-1.5">{title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== STATS BAR ====== */}
      <section className="py-12 border-y border-white/8 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-8 px-6">
          {STATS.map(({ value, label, color }) => (
            <div key={label} className="text-center">
              <p className={`text-3xl sm:text-4xl font-bold bg-gradient-to-r ${color} bg-clip-text text-transparent`}>
                {value}
              </p>
              <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ====== HOW IT WORKS ====== */}
      <section className="py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-cyan-400 text-sm font-medium tracking-widest uppercase mb-3">Simple as 1-2-3</p>
            <h2 className="text-3xl sm:text-4xl font-bold">
              How Explorio{" "}
              <span className="text-slate-400">works</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Plan your trip", desc: "Use AI to generate itineraries, find sights, and build your perfect route." },
              { step: "02", title: "Organize everything", desc: "Store tickets, bookings, documents, and budgets — all linked to your trips." },
              { step: "03", title: "Travel with confidence", desc: "Get automatic reminders for passports, events, and important dates." },
            ].map(({ step, title, desc }) => (
              <div key={step} className="relative">
                <span className="text-6xl font-bold text-white/[0.03] absolute -top-4 -left-2">{step}</span>
                <div className="relative pt-8">
                  <h3 className="text-lg font-semibold mb-2">{title}</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== LARGE CTA IMAGE ====== */}
      <section className="relative mx-6 lg:mx-12 rounded-3xl overflow-hidden mb-20">
        <img
          src="https://images.pexels.com/photos/14783333/pexels-photo-14783333.jpeg?auto=compress&cs=tinysrgb&w=1200"
          alt="Adventure awaits"
          className="w-full h-64 sm:h-80 lg:h-96 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0f1a]/90 to-[#0a0f1a]/30 flex items-center">
          <div className="px-8 lg:px-14 max-w-lg">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">Ready to explore?</h2>
            <p className="text-slate-300 text-sm mb-6">Join travelers who plan smarter with Explorio.</p>
            <Button
              onClick={scrollToAuth}
              className="rounded-full bg-white text-slate-900 hover:bg-slate-100 font-medium px-6 h-10"
            >
              Get Started Free
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* ====== FOOTER ====== */}
      <footer className="border-t border-white/8 py-10 px-6">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo192.png" alt="Explorio" className="w-7 h-7 rounded-lg" />
            <span className="text-sm font-semibold">Explorio</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-slate-600">
            <button onClick={() => navigate("/privacy")} className="hover:text-slate-400 transition-colors">Privacy</button>
            <span>Made with care for travelers</span>
          </div>
          <p className="text-xs text-slate-700">explorio.earth</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
